/** @format */

import React, { useState, useRef, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import { Theme } from "@mui/material/styles";
import logoImage from "../assets/logo_bw.png";
import { Link, useLocation } from "react-router-dom";

const useStyles = makeStyles((theme: Theme) => ({
  appBar: {
    backgroundColor: "transparent",
    boxShadow: "none",
    padding: theme.spacing(2, 0),
  },
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "none",
  },
  logo: {
    display: "flex",
    alignItems: "center",
  },
  logoImage: {
    width: "22px",
    height: "26px",
    marginRight: theme.spacing(1),
  },
  logoText: {
    color: "#ffffff",
    fontSize: "1.5rem !important",
    fontWeight: "400 !important",
  },
  navButtons: {
    display: "flex",
    position: "relative",
    [theme.breakpoints.down("md")]: {
      display: "none", // Hide regular navigation on mobile
    },
  },
  navButton: {
    color: "#ffffff !important",
    marginLeft: theme.spacing(1),
    textTransform: "none",
    fontSize: "1.2rem !important",
    fontWeight: 300,
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  activeNavButton: {
    // Remove the border-bottom from here
  },
  navLink: {
    textDecoration: "none",
  },
  borderBottom: {
    position: "absolute",
    bottom: -2,
    left: 0,
    width: "0",
    height: "2px",
    backgroundColor: "#ffffff",
    transition: "all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1.0)",
  },
  menuButton: {
    display: "none", // Hide by default
    color: "#ffffff",
    [theme.breakpoints.down("md")]: {
      display: "block", // Only show on mobile
    },
  },
  drawerPaper: {
    backgroundColor: "#2a2a2a", // Slightly lighter than primary color for better contrast
    width: "100%",
    maxWidth: "300px",
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(1),
  },
  drawerList: {
    padding: theme.spacing(2),
  },
  drawerItem: {
    marginBottom: theme.spacing(2),
  },
  drawerLink: {
    textDecoration: "none",
    color: "#ffffff",
    fontSize: "1.2rem",
    fontWeight: 300,
    fontFamily: "Poppins, sans-serif",
    width: "100%",
    display: "block",
    padding: theme.spacing(1, 2),
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)", // Increased opacity for better visibility
    },
  },
  activeDrawerLink: {
    borderLeft: "2px solid #ffffff",
    backgroundColor: "rgba(255, 255, 255, 0.15)", // Increased opacity for active item
  },
  // Add specific styling for the ListItemText component
  drawerItemText: {
    "& .MuiListItemText-primary": {
      color: "#ffffff", // Force text color to white
      fontWeight: 400,
      fontSize: "1.2rem",
    },
  },
}));

const Header: React.FC = () => {
  const classes = useStyles();
  const location = useLocation();
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [borderStyle, setBorderStyle] = useState({});
  const navButtonsRef = useRef<HTMLDivElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  useEffect(() => {
    if (hoveredButton && navButtonsRef.current) {
      const buttonElement = navButtonsRef.current.querySelector(
        `[data-button="${hoveredButton}"]`
      ) as HTMLElement;
      if (buttonElement) {
        const buttonRect = buttonElement.getBoundingClientRect();
        const containerRect = navButtonsRef.current.getBoundingClientRect();
        const left = buttonRect.left - containerRect.left;
        setBorderStyle({
          left: `${left}px`,
          width: `${buttonRect.width}px`,
        });
      }
    } else {
      setBorderStyle({ width: 0 });
    }
  }, [hoveredButton]);

  useEffect(() => {
    if (!isMobile && mobileOpen) {
      setMobileOpen(false);
    }
  }, [isMobile, mobileOpen]);

  const handleMouseEnter = (buttonName: string) => {
    setHoveredButton(buttonName);
  };

  const handleMouseLeave = () => {
    setHoveredButton(null);
  };

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    }
  };

  // Close drawer when a link is clicked
  const handleLinkClick = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <div>
      <Box className={classes.drawerHeader}>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon sx={{ color: "#ffffff" }} />
        </IconButton>
      </Box>
      <List className={classes.drawerList}>
        <ListItem disablePadding className={classes.drawerItem}>
          <Link
            to="/"
            className={`${classes.drawerLink} ${
              location.pathname === "/" ? classes.activeDrawerLink : ""
            }`}
            onClick={handleLinkClick}
          >
            <ListItemText primary="Home" className={classes.drawerItemText} />
          </Link>
        </ListItem>
        <ListItem disablePadding className={classes.drawerItem}>
          <Link
            to="/about"
            className={`${classes.drawerLink} ${
              location.pathname === "/about" ? classes.activeDrawerLink : ""
            }`}
            onClick={handleLinkClick}
          >
            <ListItemText
              primary="About Us"
              className={classes.drawerItemText}
            />
          </Link>
        </ListItem>
        <ListItem disablePadding className={classes.drawerItem}>
          <Link
            to="/pilot-test"
            className={`${classes.drawerLink} ${
              location.pathname === "/pilot-test"
                ? classes.activeDrawerLink
                : ""
            }`}
            onClick={handleLinkClick}
          >
            <ListItemText
              primary="Join Waitlist"
              className={classes.drawerItemText}
            />
          </Link>
        </ListItem>
        <ListItem disablePadding className={classes.drawerItem}>
          <Link
            to="/contact"
            className={`${classes.drawerLink} ${
              location.pathname === "/contact" ? classes.activeDrawerLink : ""
            }`}
            onClick={handleLinkClick}
          >
            <ListItemText
              primary="Contact"
              className={classes.drawerItemText}
            />
          </Link>
        </ListItem>
      </List>
    </div>
  );

  return (
    <AppBar
      position="absolute"
      className={classes.appBar}
      sx={{ boxShadow: "none" }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters className={classes.container}>
          <Link to="/" className={classes.navLink}>
            <Box className={classes.logo}>
              <img
                src={logoImage}
                alt="EON.HEALTH Logo"
                className={classes.logoImage}
              />
              <Typography className={classes.logoText}>EON.HEALTH</Typography>
            </Box>
          </Link>

          {/* Desktop navigation */}
          <Box
            className={classes.navButtons}
            ref={navButtonsRef}
            onMouseLeave={handleMouseLeave}
          >
            <Link to="/" className={classes.navLink}>
              <Button
                className={`${classes.navButton} ${
                  location.pathname === "/" ? classes.activeNavButton : ""
                }`}
                sx={{
                  marginRight: 4,
                  textTransform: "none",
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 300,
                }}
                onMouseEnter={() => handleMouseEnter("home")}
                data-button="home"
              >
                Home
              </Button>
            </Link>
            <Link to="/about" className={classes.navLink}>
              <Button
                className={`${classes.navButton} ${
                  location.pathname === "/about" ? classes.activeNavButton : ""
                }`}
                sx={{
                  marginRight: 4,
                  textTransform: "none",
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 300,
                }}
                onMouseEnter={() => handleMouseEnter("about")}
                data-button="about"
              >
                About Us
              </Button>
            </Link>
            <Link to="/join" className={classes.navLink}>
              <Button
                className={`${classes.navButton} ${
                  location.pathname === "/join" ? classes.activeNavButton : ""
                }`}
                sx={{
                  marginRight: 4,
                  textTransform: "none",
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 300,
                }}
                onMouseEnter={() => handleMouseEnter("pilotTest")}
                data-button="pilotTest"
              >
                Join Waitlist
              </Button>
            </Link>
            <Link to="/contact" className={classes.navLink}>
              <Button
                className={`${classes.navButton} ${
                  location.pathname === "/contact"
                    ? classes.activeNavButton
                    : ""
                }`}
                sx={{
                  textTransform: "none",
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 300,
                }}
                onMouseEnter={() => handleMouseEnter("contact")}
                data-button="contact"
              >
                Contact
              </Button>
            </Link>
            <div className={classes.borderBottom} style={borderStyle} />
          </Box>

          {/* Mobile menu icon - only shown when isMobile is true */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              className={classes.menuButton}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </Container>

      {/* Mobile drawer - only rendered on mobile */}
      {isMobile && (
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          classes={{
            paper: classes.drawerPaper,
          }}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            "& .MuiDrawer-paper": {
              backgroundColor: "#2a2a2a !important",
              color: "#ffffff !important",
            },
          }}
        >
          {drawer}
        </Drawer>
      )}
    </AppBar>
  );
};

export default Header;
